import { useState, useEffect, useRef, useContext } from "react";
import { getExcalidrawCanvas } from "../utils/canvas";
import { ExcalidrawWrapperContext } from "../WhiteboardCustomUI";
/**
 * Custom hook for managing Excalidraw tools and interactions
 *
 * Features:
 * - Tool switching and state management
 * - Custom pan tool implementation with drag functionality
 * - Auto-return to selection tool for shape and text tools
 * - Cursor management for different tools
 * - Text editing completion detection
 *
 * @param excalidrawApi - The Excalidraw imperative API instance
 * @returns Object containing current tool and setTool function
 */
export function useTools(excalidrawApi) {
    // =============================================
    // STATE MANAGEMENT
    // =============================================
    const [currentTool, setCurrentTool] = useState("selection");
    const { zoom } = useContext(ExcalidrawWrapperContext);
    // Refs for pan functionality
    const isDragging = useRef(false);
    const panStart = useRef({ x: 0, y: 0, scrollX: 0, scrollY: 0 });
    // =============================================
    // TOOL SWITCHING FUNCTIONALITY
    // =============================================
    /**
     * Changes the active tool and handles special behaviors
     *
     * @param newTool - The tool to switch to
     */
    function setTool(newTool) {
        if (currentTool === newTool)
            return;
        const canvas = getExcalidrawCanvas();
        // 1. Switch to the new tool
        if (newTool !== "pan") {
            excalidrawApi.setActiveTool({ type: newTool });
        }
        else {
            // Pan is implemented as a custom tool
            excalidrawApi.setActiveTool({ type: "custom", customType: "pan" });
        }
        setCurrentTool(newTool);
        // 2. Auto-return to selection for shape and text tools after use
        if (canvas && (newTool === "ellipse" || newTool == 'arrow' || newTool === "rectangle" || newTool === "text")) {
            const finishDrawing = () => {
                // Return to selection tool
                excalidrawApi.setActiveTool({ type: "selection" });
                setCurrentTool("selection");
                // Clean up the event listener
                canvas.removeEventListener("pointerup", finishDrawing);
            };
            // Listen for pointer up event (shape/text creation completion)
            canvas.addEventListener("pointerup", finishDrawing);
        }
    }
    // =============================================
    // PAN TOOL EVENT HANDLERS
    // =============================================
    /**
     * Handles pointer down event for pan tool - initiates dragging
     */
    function handlePanOnPointerDown(e) {
        e.preventDefault();
        e.stopPropagation();
        const canvas = getExcalidrawCanvas();
        if (!canvas)
            return;
        // Get current scroll position from Excalidraw's state
        const { scrollX, scrollY } = excalidrawApi.getAppState();
        isDragging.current = true;
        panStart.current = {
            x: e.clientX,
            y: e.clientY,
            scrollX,
            scrollY,
        };
    }
    /**
     * Handles pointer up event for pan tool - stops dragging
     */
    function handlePanOnPointerUp(e) {
        e.preventDefault();
        e.stopPropagation();
        isDragging.current = false;
    }
    /**
     * Handles pointer move event for pan tool - performs the panning
     */
    function handlePanOnPointerMove(e) {
        if (!isDragging.current)
            return;
        e.preventDefault();
        e.stopPropagation();
        // Calculate movement delta
        const dx = e.clientX - panStart.current.x;
        const dy = e.clientY - panStart.current.y;
        // Update scroll position considering zoom level
        const nextScrollX = panStart.current.scrollX + dx / zoom;
        const nextScrollY = panStart.current.scrollY + dy / zoom;
        // Update Excalidraw's scene with new scroll position
        excalidrawApi.updateScene({
            appState: {
                scrollX: nextScrollX,
                scrollY: nextScrollY,
            },
        });
    }
    // =============================================
    // EFFECT HOOKS
    // =============================================
    /**
     * Reset dragging state when zoom changes
     */
    useEffect(() => {
        isDragging.current = false;
    }, [zoom]);
    /**
     * Setup pan tool event listeners
     */
    useEffect(() => {
        const canvas = getExcalidrawCanvas();
        if (!canvas || currentTool !== "pan")
            return;
        // Attach pan event listeners
        canvas.addEventListener("pointerdown", handlePanOnPointerDown);
        canvas.addEventListener("pointerup", handlePanOnPointerUp);
        canvas.addEventListener("pointermove", handlePanOnPointerMove);
        // Cleanup function
        return () => {
            canvas.removeEventListener("pointermove", handlePanOnPointerMove);
            canvas.removeEventListener("pointerdown", handlePanOnPointerDown);
            canvas.removeEventListener("pointerup", handlePanOnPointerUp);
        };
    }, [currentTool, zoom]);
    /**
     * Manage cursor appearance for pan tool
     */
    useEffect(() => {
        const canvas = getExcalidrawCanvas();
        if (!canvas)
            return;
        if (currentTool === "pan") {
            // Set initial cursor
            canvas.style.cursor = isDragging.current ? "grabbing" : "grab";
            /**
             * Updates cursor based on dragging state
             */
            const updateCursor = (e) => {
                canvas.style.cursor = isDragging.current ? "grabbing" : "grab";
            };
            // Cursor event handlers
            const onPointerDown = () => {
                isDragging.current = true;
                canvas.style.cursor = "grabbing";
            };
            const onPointerUp = () => {
                isDragging.current = false;
                canvas.style.cursor = "grab";
            };
            // Attach cursor management listeners
            canvas.addEventListener("pointermove", updateCursor);
            canvas.addEventListener("pointerdown", onPointerDown);
            canvas.addEventListener("pointerup", onPointerUp);
            // Cleanup function
            return () => {
                canvas.style.cursor = "";
                canvas.removeEventListener("pointermove", updateCursor);
                canvas.removeEventListener("pointerdown", onPointerDown);
                canvas.removeEventListener("pointerup", onPointerUp);
            };
        }
        else {
            // Reset cursor for other tools
            canvas.style.cursor = "";
        }
    }, [currentTool]);
    /**
     * Handle text editing completion - auto-return to selection tool
     * when user finishes editing text by clicking outside the text editor
     */
    useEffect(() => {
        // Only handle this when in text mode
        if (currentTool !== "text") {
            return;
        }
        /**
         * Detects when text editing is finished and returns to selection tool
         */
        const onFocusOut = (e) => {
            const target = e.target;
            // Check if the focus is leaving Excalidraw's text editor
            if (target.tagName === "TEXTAREA" &&
                target.closest(".excalidraw-textEditorContainer")) {
                // Return to selection tool
                excalidrawApi.setActiveTool({ type: "selection" });
                setCurrentTool("selection");
                // Clean up listener
                document.removeEventListener("focusout", onFocusOut, true);
            }
        };
        // Listen for focus out events globally (with capture)
        document.addEventListener("focusout", onFocusOut, true);
        // Cleanup function
        return () => {
            document.removeEventListener("focusout", onFocusOut, true);
        };
    }, [currentTool, excalidrawApi]);
    // =============================================
    // RETURN API
    // =============================================
    return {
        currentTool,
        setTool,
    };
}
